import {
  CreateSphereDialogProps,
  useSphereDialog,
} from "@components/common/dialog/sphere-dialog-provider";
import { FARO_CARD_ACTIONS } from "@components/common/faro-card/faro-card";
import { ACTION_BTN_SIZE, ACTION_ICON_SIZE } from "@styles/common-styles";
import { SphereActionDivider } from "@components/common/sphere-action-divider";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { SphereShareLink } from "@components/common/sphere-share-link";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { ShareLinkIcon, ViewerIcon } from "@flat-ui";
import { Box } from "@mui/material";
import { selectedProjectSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import {
  OPEN_OTHERS_TARGET_ATTRIBUTE,
  OpenProjectProps,
  trackOpenProject,
} from "@utils/project-utils";
import { PointCloudEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { MouseEvent, useCallback } from "react";

/**
 * Dialog provider props to use for the share annotation dialog.
 */
const SHARE_POINT_CLOUD_DIALOG_PROVIDER_PROPS: CreateSphereDialogProps = {
  title: "Share point cloud",
  isHideConfirm: true,
  isHideCancel: true,
};

interface Props {
  /** URL of the Viewer deep-link to open/share the point cloud */
  sphereViewerUrl: string;

  /** Show the divider */
  isDividerVisible?: boolean;
}

/** Component that renders the action buttons column for the point clouds table */
export function PointCloudActions({
  sphereViewerUrl,
  isDividerVisible = true,
}: Props): JSX.Element {
  const { createDialog } = useSphereDialog();
  const { trackEvent, trackAsyncEvent } = useTrackEvent();
  const selectedProject = useAppSelector(selectedProjectSelector);

  /**
   * Opens the point cloud in the Viewer and tracks the event
   */
  function onOpenPointCloud(
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
    clickType: OpenProjectProps["clickType"]
  ): void {
    event.stopPropagation();

    void trackOpenProject({
      openFrom: "projectDetails-data-pointClouds",
      clickType,
      numberOfMembers: selectedProject?.members.length,
      openTarget: ProjectLaunchTarget.sphereViewer,
      ...selectedProject,
      trackAsyncEvent,
    });
  }

  /** Triggered to share a markup by deep-link */
  const onSharePointCloud = useCallback((): void => {
    trackEvent({ name: PointCloudEvents.sharePointCloud });

    void createDialog(
      SHARE_POINT_CLOUD_DIALOG_PROVIDER_PROPS,
      <SphereShareLink
        title="Point cloud link"
        url={sphereViewerUrl}
        copyLogEventParams={{ name: PointCloudEvents.copySharePointCloudLink }}
      />
    );
  }, [createDialog, sphereViewerUrl, trackEvent]);

  return (
    <Box
      component="div"
      className={FARO_CARD_ACTIONS}
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <SphereTooltip title="Share point cloud">
        <SphereIconButton
          buttonSize={ACTION_BTN_SIZE}
          iconSize={ACTION_ICON_SIZE}
          component={ShareLinkIcon}
          onClick={onSharePointCloud}
        />
      </SphereTooltip>

      {isDividerVisible && <SphereActionDivider />}

      <SphereTooltip title="Open point cloud">
        <SphereIconButton
          buttonSize={ACTION_BTN_SIZE}
          iconSize={ACTION_ICON_SIZE}
          component={ViewerIcon}
          href={sphereViewerUrl}
          target={OPEN_OTHERS_TARGET_ATTRIBUTE}
          onClick={(event) => onOpenPointCloud(event, "left button click")}
          onAuxClick={(event) => onOpenPointCloud(event, "middle button click")}
          onContextMenu={(event) =>
            onOpenPointCloud(event, "open context menu")
          }
        />
      </SphereTooltip>
    </Box>
  );
}
